import * as React from "react";
import { Skeleton } from "@progress/kendo-react-indicators";
import styles from "./contactform.module.scss";
import { ContactFormContextProvider, useContactFormContext } from "./Context";
import { ContactFormDemoRepo } from "./stubrepo";
import { ContactRequest, OrderLookupResult, TicketInfo } from "./types";
import { LookupForm } from "./LookupForm";
import { ContactFormApiRepo } from "./apirepo";
import { ContactMessageForm } from "./ContactMessageForm";
import { OrderInfoUI } from "./OrderInfoUI";
import { getQueryString } from "../../js/modules/query-string";
import { safeInt, safeString } from "../../lib/common";

type ContactFormState =
  | "lookup"
  | "qs-lookup-performed-vendor"
  | "qs-lookup-performed-customer"
  | "message-saved"
  | "contact-form";

export const enum ContactSourceType {
  Customer = 0,
  Vendor = 1,
}

interface OrderContactMeta {
  TicketId: number;
  OrderNumber: number;
  Email: string;
  Source: number;
}

const getContactQueryString = (source: ContactSourceType): OrderContactMeta => {
  let email = safeString(getQueryString("Email"));
  if (email.length === 0) {
    email = safeString(getQueryString("em"));
  }

  const meta: OrderContactMeta = {
    TicketId: safeInt(getQueryString("ticketId")),
    OrderNumber: safeInt(getQueryString("OrderNumber")),
    Email: email,
    Source: source,
  };
  return meta;
};

/**
 * Container for the contact form.
 * Handles the management of the components: 1) order lookup, 2) order display and 3) contact message form.
 * @param props
 * @returns
 */
export const ContactFormUI: React.FC<{
  SourceType: ContactSourceType;
}> = (props) => {
  const context = useContactFormContext();

  const [formError, setError] = React.useState("");
  const [lookupResult, setLookupResult] =
    React.useState<null | OrderLookupResult>(null);
  const [isLoading, setIsLoading] = React.useState(false);

  const [viewState, setViewState] = React.useState<ContactFormState>("lookup");

  async function handleLookupRequest(
    orderNumber: number,
    email: string,
    ticketId?: number
  ): Promise<void> {
    try {
      setIsLoading(true);
      const result = await context.LookupOrder(orderNumber, email, ticketId);

      if (result.Payload) {
        result.Payload.TicketId = ticketId ?? 0;
      }

      setLookupResult(result);
      setIsLoading(false);

      if (!result.IsSuccess) {
        setError(result.Messages.join("\n"));
      } else {
        setViewState("contact-form");
      }
    } catch (err) {
      setIsLoading(false);
      if (err instanceof Error) {
        setError(err.message);
      }
      if (err instanceof Response) {
        setError(await err.text());
      }
    }
  }

  async function handleSave(request: ContactRequest): Promise<void> {
    const result = await context.SaveMessage(request);

    if (!result.IsSuccess) {
      setError(result.Messages.join("\n"));
    } else {
      setViewState("message-saved");
    }
  }

  React.useEffect(() => {
    const contact = getContactQueryString(props.SourceType);

    if (contact.Source === ContactSourceType.Vendor) {
      setViewState("qs-lookup-performed-vendor");
      setIsLoading(true);
      handleLookupRequest(contact.OrderNumber, contact.Email, contact.TicketId);
      return;
    }

    if (contact.Email.length > 0 && contact.OrderNumber > 0) {
      if (contact.Source === ContactSourceType.Vendor) {
        setViewState("qs-lookup-performed-vendor");
      } else {
        setViewState("qs-lookup-performed-customer");
      }
      setIsLoading(true);
      handleLookupRequest(contact.OrderNumber, contact.Email, contact.TicketId);
    }
  }, [props.SourceType]);

  if (isLoading) {
    return (
      <div className={styles.contactForm}>
        <Skeleton
          shape={"text"}
          style={{ width: "60%", height: "25px" }}
        ></Skeleton>
        <Skeleton
          shape={"text"}
          style={{ width: "80%", height: "50px" }}
        ></Skeleton>

        <Skeleton
          shape={"text"}
          style={{ width: "40%", height: "25px" }}
        ></Skeleton>
        <Skeleton
          shape={"rectangle"}
          style={{ width: "100%", height: "200px" }}
        ></Skeleton>
      </div>
    );
  }
  if (
    !lookupResult ||
    lookupResult.IsSuccess === false ||
    lookupResult.Payload === null
  ) {
    return (
      <>
        {(viewState === "lookup" ||
          viewState === "qs-lookup-performed-customer") && (
          <LookupForm onLookup={handleLookupRequest}></LookupForm>
        )}
        {formError && viewState.startsWith("qs-lookup-performed") && (
          <div className={styles.error}>Invalid URL: {formError}</div>
        )}
        {formError && viewState === "lookup" && (
          <div className={styles.error}>{formError}</div>
        )}
      </>
    );
  }

  function resetForm(): void {
    setViewState("contact-form");
  }

  function handleTicketEdit(ticketInfo: TicketInfo): void {
    handleLookupRequest(
      orderInfo.OrderNumber,
      orderInfo.Email,
      ticketInfo.TicketId
    );
  }

  const orderInfo = lookupResult.Payload;

  function handlePerformRefresh(ticketId: number): void {
    handleLookupRequest(orderInfo.OrderNumber, orderInfo.Email, ticketId);
  }

  return (
    <>
      <OrderInfoUI
        OrderInfo={lookupResult.Payload}
        SourceType={props.SourceType}
      ></OrderInfoUI>
      {props.SourceType === ContactSourceType.Customer &&
        orderInfo.OpenTickets.length > 0 && (
          <div className={styles.ticketContainer}>
            <label>You have open issues</label>
            {orderInfo.OpenTickets.map((ticketInfo, ix) => {
              return (
                <div key={ticketInfo.TicketId} className={styles.ticketItem}>
                  Issue #{ix + 1}: {ticketInfo.TicketName}
                  {ticketInfo.TicketId !== orderInfo.TicketId && (
                    <button
                      className={styles.linkButton}
                      onClick={() => handleTicketEdit(ticketInfo)}
                    >
                      I would like to comment on this issue.
                    </button>
                  )}
                  {ticketInfo.TicketId === orderInfo.TicketId && (
                    <span>You are commenting on this issue.</span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      {viewState === "message-saved" && (
        <div className={styles.resultContainer}>
          <div className={styles.success}>
            Thank you! Your message has been submitted to our customer service
            team.
          </div>
          <button className={styles.lookupButton} onClick={resetForm}>
            Submit Another Message
          </button>
        </div>
      )}
      {viewState === "contact-form" && (
        <ContactMessageForm
          OrderInfo={lookupResult.Payload}
          onMessageSave={handleSave}
          Source={props.SourceType}
          onNeedRefresh={handlePerformRefresh}
        ></ContactMessageForm>
      )}
    </>
  );
};

export const VendorContactForm = () => {
  return (
    <div className={styles.contactContainer}>
      <h2>Vendor Contact Form</h2>
      <ContactFormContextProvider value={ContactFormApiRepo}>
        <ContactFormUI SourceType={ContactSourceType.Vendor}></ContactFormUI>
      </ContactFormContextProvider>
    </div>
  );
};
export const CustomerContactForm = () => {
  return (
    <div className={styles.contactContainer}>
      <h1 className={styles.contactContainer__title}>
        Track an Order /<wbr /> Contact Customer Service
      </h1>
      <ContactFormContextProvider value={ContactFormApiRepo}>
        <ContactFormUI SourceType={ContactSourceType.Customer}></ContactFormUI>
      </ContactFormContextProvider>
    </div>
  );
};
